/**
 * Option Plus price display + cart property fix
 * ---------------------------------------------------------------------------
 * The Option Plus app's own script accumulates the price delta incrementally
 * and doesn't always reset it when a shopper switches from a priced option
 * back to a free one:
 *  - #price-display-container keeps showing the old total.
 *  - the hidden `total_option_price` cart line-item property input keeps the
 *    stale value, which then gets submitted to /cart/add and shown in cart.
 *
 * This recalculates the total from scratch on every dropdown change: sum the
 * data-price of whichever .dropdown-option is currently selected in each
 * option group, across the WHOLE #optionPlusvariant-container, then write
 * that total (not an incremental delta) into both the price banner and every
 * hidden `total_option_price` input. Also re-syncs immediately before the
 * product form submits, so a stale value can never reach the cart even in an
 * edge case the observers below miss. Never touches the app's markup — only
 * reads it and overwrites text/value.
 */
(() => {
  const CONTAINER_ID = 'optionPlusvariant-container';
  const PRICE_DISPLAY_ID = 'price-display-container';
  const SELECT_SELECTOR = 'select[data-title]';
  const TOTAL_OPTION_PRICE_SELECTOR =
    'input[name*="total_option_price" i], input[id*="total_option_price" i]';

  function priceFromDropdownOption(container, select) {
    const title = select.dataset.title;
    const value = select.value;
    if (!title || !value) return 0;

    const group = container.querySelector(
      `.searchable-dropdown[data-title="${cssEscape(title)}"]`,
    );
    if (!group) return 0;

    const option = Array.from(group.querySelectorAll('.dropdown-option')).find(
      (el) => el.dataset.value === value,
    );
    if (!option) return 0;

    const price = parseFloat(option.dataset.price);
    return Number.isFinite(price) ? price : 0;
  }

  function cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&');
  }

  function calculateTotalDelta(container) {
    const selects = Array.from(container.querySelectorAll(SELECT_SELECTOR));
    return selects.reduce((total, select) => total + priceFromDropdownOption(container, select), 0);
  }

  function formatMoney(amount) {
    return `$${amount.toFixed(2)}`;
  }

  function render(priceDisplay, delta) {
    const banner = priceDisplay.querySelector('.price-display');
    if (!banner) return false;

    const nextHidden = delta === 0;
    const nextText =
      delta > 0
        ? `Cart Price will Increment By: ${formatMoney(delta)}`
        : delta < 0
          ? `Cart Price will Decrease By: ${formatMoney(Math.abs(delta))}`
          : '';

    if (banner.hidden === nextHidden && banner.textContent === nextText) {
      return false;
    }

    banner.hidden = nextHidden;
    banner.textContent = nextText;
    return true;
  }

  // Syncs every matching hidden `total_option_price` input (there may be more
  // than one — e.g. one inside the app block and a duplicate proxy in the
  // product form) to the freshly computed total, in "amount.00" form. Skips
  // writing when the value already matches, same reasoning as render() above.
  function syncTotalOptionPriceInputs(delta) {
    const nextValue = delta.toFixed(2);
    document.querySelectorAll(TOTAL_OPTION_PRICE_SELECTOR).forEach((input) => {
      if (input.value === nextValue) return;
      input.value = nextValue;
    });
  }

  class OptionPlusPrice {
    constructor(container, priceDisplay) {
      this.container = container;
      this.priceDisplay = priceDisplay;
      this.recalculate = this.recalculate.bind(this);
      this.isRendering = false;

      container.addEventListener('change', (evt) => {
        if (evt.target.matches(SELECT_SELECTOR)) this.recalculate();
      });
      container.addEventListener('click', (evt) => {
        if (evt.target.closest('.dropdown-option')) this.recalculate();
      });

      // Watch selection changes on the option groups only — never the
      // price-display node itself — so writing the price text can never
      // re-trigger this same observer (that self-feedback loop is what
      // froze the page).
      this.observer = new MutationObserver(this.recalculate);
      container.querySelectorAll(SELECT_SELECTOR).forEach((select) => {
        this.observer.observe(select, { childList: true, attributes: true, attributeFilter: ['selected', 'value'] });
      });
      container.querySelectorAll('.searchable-dropdown').forEach((dropdown) => {
        this.observer.observe(dropdown, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      });

      this.attachSubmitGuard();
      this.recalculate();
    }

    // Last-resort safety net: re-syncs the hidden total_option_price input(s)
    // in the submit event's capture phase, immediately before the browser
    // builds the FormData for /cart/add, so no stale value can ever reach
    // the cart even if some observer above missed a change.
    attachSubmitGuard() {
      const form = this.container.closest('form') || document.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      form.addEventListener(
        'submit',
        () => {
          syncTotalOptionPriceInputs(calculateTotalDelta(this.container));
        },
        { capture: true },
      );
    }

    recalculate() {
      // Re-entrancy guard: belt-and-suspenders alongside the scoped
      // observer targets above, in case the app re-creates dropdown nodes
      // (new elements, new observer targets) as part of handling a change.
      if (this.isRendering) return;
      this.isRendering = true;
      try {
        const total = calculateTotalDelta(this.container);
        render(this.priceDisplay, total);
        syncTotalOptionPriceInputs(total);
      } finally {
        this.isRendering = false;
      }
    }
  }

  function attach() {
    const container = document.getElementById(CONTAINER_ID);
    const priceDisplay = document.getElementById(PRICE_DISPLAY_ID);
    if (!container || !priceDisplay) return false;
    new OptionPlusPrice(container, priceDisplay);
    return true;
  }

  function init() {
    if (attach()) return;

    const bodyObserver = new MutationObserver(() => {
      if (attach()) bodyObserver.disconnect();
    });
    bodyObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
